import Vue from 'vue';
import { mapActions, mapState } from 'vuex';
import { RUNNITS_OWNER_SELECTION } from '@/views/Runnits/constants';
import { RunnitsOwnerSelection, RunnitState } from '@/store';
import { LimitClubAddOn, RunnitsAccessLevel, Team, User } from '@run-diffusion/shared';

export const RunnitsAndRunnitNodeDefsBindingsMixin = Vue.extend({
	computed: {
		...mapState([
			'user',
			'team',
			'runnitState',
		]),
		/*
		The plan's board cap, as a primitive. limitNumRunnits is bound as a `.limit(N)` firestore
		query where N is this cap, so a plan change has to re-run it — the locked-row state and the
		"x/y boards used" counter both read that result set, and neither can notice a cap change on
		its own. Keyed on the values rather than the clubInfo.limit object because `user` is a
		Vuefire binding that emits a fresh object on every unrelated write to the user doc.
		 */
		userRunnitsLimitKey (): string {
			const limit: LimitClubAddOn = this._get(this.user, 'clubInfo.limit') || null;
			if (!limit) return 'none';
			return `${!!limit.unlimitedRunnits}::${limit.numRunnitsLimit || 0}`;
		},
	},
	watch: {
		runnitState: {
			immediate: true,
			handler (newVal: RunnitState, oldVal: RunnitState) {
				if (
					newVal &&
					oldVal
				) {
					const ownerSelectionChanged: boolean = newVal.runnitsOwnerSelection && newVal.runnitsOwnerSelection !== oldVal.runnitsOwnerSelection
					const accessLevelChanged: boolean = newVal.runnitsAccessLevel && newVal.runnitsAccessLevel !== oldVal.runnitsAccessLevel

					if (ownerSelectionChanged || accessLevelChanged) {
						this.doRunnitsAndRunnitNodeDefsBindings(newVal.runnitsOwnerSelection, newVal.runnitsAccessLevel, this.user, this.team);
						this.doModelsBindings(newVal.runnitsOwnerSelection, newVal.runnitsAccessLevel, this.user, this.team);
					}
				}
			},
		},
		team: {
			immediate: true,
			handler (newVal: Team, oldVal: Team) {
				if (this.runnitState && newVal !== oldVal) {
					this.doRunnitsAndRunnitNodeDefsBindings(this.runnitState.runnitsOwnerSelection, this.runnitState.runnitsAccessLevel, this.user, newVal);
					this.doModelsBindings(this.runnitState.runnitsOwnerSelection, this.runnitState.runnitsAccessLevel, this.user, newVal);
				}
			},
		},
		/*
		Upgrade/downgrade mid-session. Only the limitNumRunnits query depends on the cap, so this
		rebinds that one listener rather than calling doRunnitsAndRunnitNodeDefsBindings, which would
		needlessly tear down and re-establish five unrelated bindings. Not immediate: the initial
		bind is already covered by the watchers above.
		 */
		userRunnitsLimitKey: {
			immediate: false,
			handler () {
				this.rebindLimitNumRunnits();
			},
		},
	},
	methods: {
		...mapActions([
			'updateRunnitState',
			'bindLimitNumRunnits',
			'unbindLimitNumRunnits',
			'bindTrainerNodeDefs',
			'bindPublicRunnitNodeDefs',
			'bindCompanyRunnitNodeDefs',
			'bindPublicRunnitNodeDefTags',
			'bindRunnitKits',
			'bindRecentUserRunnitNodeDefInfos',
			'bindRecentUserWorkflowRunnitInfos',
			'bindModels',
		]),
		// Same payload doRunnitsAndRunnitNodeDefsBindings sends, scoped to the one cap-dependent bind
		rebindLimitNumRunnits () {
			if (!this.user || !this.runnitState) return;
			const onTeam: boolean = this.runnitState.runnitsOwnerSelection === RUNNITS_OWNER_SELECTION.TEAM;
			if (onTeam && !this.team) return;
			this.bindLimitNumRunnits({
				userId: this.user.id,
				teamId: onTeam ? this.team.id : null,
				accessLevel: this.runnitState.runnitsAccessLevel,
				limitClubAddOn: this.user.clubInfo?.limit || null,
			});
		},
		doRunnitsAndRunnitNodeDefsBindings (runnitsOwnerSelection: RunnitsOwnerSelection, accessLevel: RunnitsAccessLevel, user: User, team: Team) {
			if (runnitsOwnerSelection === RUNNITS_OWNER_SELECTION.TEAM && !team) {
				return;
			}

			this.bindTrainerNodeDefs();
			this.bindPublicRunnitNodeDefs();
			this.bindPublicRunnitNodeDefTags();
			// Kits are curated over the node defs bound just above, so they bind
			// on the same trigger and are never a render behind them.
			this.bindRunnitKits();

			if (user) {
				this.bindLimitNumRunnits({
					userId: user.id,
					teamId: runnitsOwnerSelection === RUNNITS_OWNER_SELECTION.TEAM && team ? team.id : null,
					accessLevel,
					limitClubAddOn: user.clubInfo?.limit || null,
				});
				this.bindCompanyRunnitNodeDefs(team ? team.companyId : null);
				this.bindRecentUserRunnitNodeDefInfos(user);
				this.bindRecentUserWorkflowRunnitInfos({
					user,
					team: (runnitsOwnerSelection === RUNNITS_OWNER_SELECTION.TEAM && team) || null,
				});
			}
		},
		doModelsBindings (runnitsOwnerSelection: RunnitsOwnerSelection, accessLevel: RunnitsAccessLevel, user: User, team: Team) {
			if (runnitsOwnerSelection === RUNNITS_OWNER_SELECTION.TEAM && !team) {
				return;
			}

			if (user) {
				this.bindModels({
					userId: user.id,
					teamId: runnitsOwnerSelection === RUNNITS_OWNER_SELECTION.TEAM && team ? team.id : null,
					accessLevel,
				});
			}
		}
	},
});
